import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { usePagedList } from '@/lib/use-paged-list'
import { branchApi } from '@/modules/branch/branch.service'
import { whatsappApi } from './whatsapp.service'
import type {
  CreateSessionInput,
  ListMessagesParams,
  SendMessageInput,
} from './whatsapp.types'

const KEY = 'whatsapp-sessions'
const MESSAGES_KEY = 'whatsapp-messages'
const DEFAULT_KEY = 'whatsapp-default-session'

// The active branch's default WhatsApp session id (null if none).
export function useDefaultWhatsappSession() {
  return useQuery({ queryKey: [DEFAULT_KEY], queryFn: branchApi.getDefaultWhatsapp })
}

export function useWhatsappSessions(enabled = true, refetchIntervalMs?: number) {
  return useQuery({
    queryKey: [KEY],
    queryFn: whatsappApi.listSessions,
    enabled,
    refetchInterval: refetchIntervalMs ?? false,
  })
}

// ¿La sucursal puede enviar WhatsApp ahora mismo? Sin sesión conectada el envío
// falla del lado de Wasender, así que conviene apagar el botón antes que
// mostrar un error después.
export function useWhatsappReady(): { ready: boolean; reason?: string } {
  const { data: defaultSessionId } = useDefaultWhatsappSession()
  const { data: sessions } = useWhatsappSessions()

  if (!sessions) return { ready: false, reason: 'Verificando la sesión de WhatsApp…' }
  if (!defaultSessionId) {
    return { ready: false, reason: 'La sucursal no tiene una sesión de WhatsApp configurada' }
  }
  const session = sessions.find((item) => String(item.id) === String(defaultSessionId))
  if (session?.status !== 'connected') {
    return { ready: false, reason: 'La sesión de WhatsApp está desconectada' }
  }
  return { ready: true }
}

// Poll a single session's status (used while the connect/QR modal is open).
export function useWhatsappSession(id: number | null, enabled: boolean) {
  return useQuery({
    queryKey: [KEY, id],
    queryFn: () => whatsappApi.getSession(id as number),
    enabled: enabled && id != null,
    refetchInterval: enabled ? 3000 : false,
  })
}

// The QR string; refetch before the ~45s expiry while the modal is open.
export function useQrCode(id: number | null, enabled: boolean) {
  return useQuery({
    queryKey: [KEY, id, 'qr'],
    queryFn: () => whatsappApi.qrCode(id as number),
    enabled: enabled && id != null,
    refetchInterval: enabled ? 20000 : false,
  })
}

function invalidate(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: [KEY] })
}

export function useCreateSession() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: CreateSessionInput) => whatsappApi.createSession(data),
    onSuccess: () => {
      invalidate(queryClient)
      toast.success('Sesión creada')
    },
    onError: (error: any) => toast.error(error.message || 'No se pudo crear la sesión'),
  })
}

export function useUpdateSession() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: CreateSessionInput }) =>
      whatsappApi.updateSession(id, data),
    onSuccess: () => {
      invalidate(queryClient)
      toast.success('Sesión actualizada')
    },
    onError: (error: any) => toast.error(error.message || 'No se pudo actualizar la sesión'),
  })
}

export function useDeleteSession() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => whatsappApi.deleteSession(id),
    onSuccess: () => {
      invalidate(queryClient)
      toast.success('Sesión eliminada')
    },
    onError: (error: any) => toast.error(error.message || 'No se pudo eliminar la sesión'),
  })
}

export function useConnectSession() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => whatsappApi.connect(id),
    onSuccess: () => invalidate(queryClient),
    onError: (error: any) => toast.error(error.message || 'No se pudo iniciar la conexión'),
  })
}

export function useDisconnectSession() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => whatsappApi.disconnect(id),
    onSuccess: () => {
      invalidate(queryClient)
      toast.success('Sesión desconectada')
    },
    onError: (error: any) => toast.error(error.message || 'No se pudo desconectar'),
  })
}

// --- Messages ------------------------------------------------------------

/** Paginated audit log of sent WhatsApp messages (branch-scoped). */
export function useWhatsappMessages(
  filters: ListMessagesParams,
  pagination: { pageIndex: number; pageSize: number },
) {
  return usePagedList([MESSAGES_KEY, filters], pagination, ({ limit, offset }) =>
    whatsappApi.listMessages({ ...filters, limit, offset }),
  )
}

export function useCheckWhatsappNumber() {
  return useMutation({
    mutationFn: ({ sessionId, phone }: { sessionId: string; phone: string }) =>
      whatsappApi.checkNumber(sessionId, phone),
  })
}

export function useSendWhatsappMessage() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      sessionId,
      message,
    }: {
      sessionId: string
      message: SendMessageInput
    }) => whatsappApi.sendMessage(sessionId, message),
    onSuccess: () => toast.success('Mensaje enviado'),
    onSettled: () => queryClient.invalidateQueries({ queryKey: [MESSAGES_KEY] }),
    onError: (error: any) =>
      toast.error(error.message || 'No se pudo enviar el mensaje'),
  })
}
